import { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useNavigate, useLocation } from 'react-router';
import { ButtonFooter, Column, Spacer, StepsHeader } from '../components';
import { Svgs } from '../assets/svg';
import { useAppStorage, useMutation, useObservable } from '@aesop-fables/scrinium';
import { useService } from '@aesop-fables/containr-react';
import { SignUpWizard, signUpWizardKey } from '../services/signUp';
import { HelpChooseProduct } from '.';
import { useDeviceType } from '../hooks/useDeviceType';
import { SignUpCompartments, signUpStorageKey } from '../data/signUp';
import { OrgSignUpStateData } from '../models/SignUpData';
import { UpdateSignUpState } from '../data/signUp/mutations';
import { LicenseAgreementData } from '../api/apis/LicenseAgreementApi';
import { LicenseCompartments, licenseStorageKey } from '../data/license';
import CancelContract from '../components/CancelContract';
import { ProductTypeEnum, isProductType } from '../models/PricebookData';
import { useLoading } from '../hooks/useLoading';
import { useBasePricebookOptions } from '../hooks/usePricebookOptions';
import ProductCard from './ProductCard';

const ChooseProduct: React.FC = () => {
  const appStorage = useAppStorage();
  const location = useLocation();
  const navigate = useNavigate();
  const { setLoading } = useLoading();
  const { isMobile, isTablet } = useDeviceType();

  const signUpWizard = useService<SignUpWizard>(signUpWizardKey);
  const updateSignUpState = useMutation(new UpdateSignUpState());

  const signUpDataCache = appStorage.retrieve<SignUpCompartments>(signUpStorageKey);
  const licenseDataCache = appStorage.retrieve<LicenseCompartments>(licenseStorageKey);

  const orgSignUpState = useObservable(
    signUpDataCache.observe$<OrgSignUpStateData>('orgSignUpState'),
  );
  const licenseAgreements = useObservable(
    licenseDataCache.observe$<LicenseAgreementData[]>('licenseAgreement'),
  );
  const multipleProductTypesAvailable = useObservable(signUpWizard.multipleProductTypes$);
  const pricebookOptions = useBasePricebookOptions();

  const [selectedProduct, setSelectedProduct] = useState<ProductTypeEnum | undefined>(undefined);
  const [recommendedProduct, setRecommendedProduct] = useState<ProductTypeEnum | undefined>(
    undefined,
  );

  const product =
    orgSignUpState?.state && orgSignUpState?.state?.product
      ? orgSignUpState?.state?.product
      : undefined;
  const pricebook =
    orgSignUpState?.state?.pricebook && JSON.parse(orgSignUpState?.state?.pricebook);
  const { activeStep, totalSteps } = signUpWizard.currentStep(location);
  const licenseAgreement =
    licenseAgreements && licenseAgreements?.find(license => license.docStatus);
  const pendingAgreement = licenseAgreement?.docStatus && !licenseAgreement?.hasAccepted;
  const activeContract = pendingAgreement || licenseAgreement?.docStatus;

  const isSelected = (product: ProductTypeEnum) => selectedProduct === product;
  const isRecommended = (product: ProductTypeEnum) => recommendedProduct === product;

  useEffect(() => {
    if (typeof activeContract === 'undefined' && !pricebook) {
      setSelectedProduct(undefined);
    }
  }, [activeContract, pricebook]);

  useEffect(() => {
    if (!pricebookOptions) return;
    if (
      isProductType(product) &&
      pricebookOptions.some(pb => pb.pricebook.productType.includes(product))
    ) {
      setSelectedProduct(product);
    } else {
      setSelectedProduct(undefined);
    }
  }, [product, pricebookOptions]);

  const setProduct = async (selectedProduct: ProductTypeEnum) => {
    await updateSignUpState.action({
      lastCompletedStep: signUpWizard.getCurrent(location),
      state: {
        ...orgSignUpState?.state,
        product: selectedProduct,
      },
    });
  };

  const navToNextStep = async () => {
    if (selectedProduct) {
      try {
        setLoading(true);
        await setProduct(selectedProduct);
        await signUpWizard.setPricebookCount(selectedProduct);
        const next = signUpWizard.nextStep(location);
        navigate(next?.route ?? '');
      } finally {
        setLoading(false);
      }
    }
  };

  const navBack = () => {
    const step = signUpWizard.previousStep(location);
    navigate(step?.route ?? '');
  };

  if (!orgSignUpState) return null;

  return (
    <Column style={{ margin: isMobile || isTablet ? '0px 20px' : '0px 100px' }}>
      <Spacer height='xs' />
      <StepsHeader
        activeStep={activeStep}
        totalSteps={totalSteps}
        headerSteps={multipleProductTypesAvailable ? 'onboardingMultipleProducts' : 'onboarding'}
      />
      <Spacer height='xs' />
      <Typography variant='h1' color='secondary'>
        Step {activeStep}: Which product are you interested in?
      </Typography>
      <Spacer height='xs' />
      <HelpChooseProduct
        recommendedProduct={recommendedProduct}
        setRecommendedProduct={setRecommendedProduct}
      />
      <Spacer height='sm' />
      <Box
        display='flex'
        flexDirection={isMobile || isTablet ? 'column' : 'row'}
        justifyContent='center'
        gap={isMobile || isTablet ? '12px' : '56px'}>
        <ProductCard
          bullets={[
            'Retirement planning',
            'Retirement saving and spending',
            'Best for desktop/laptop',
          ]}
          art={<Svgs.ArtCarouselBeachChair />}
          product={ProductTypeEnum.GuidedChoice}
          setSelectedProduct={setSelectedProduct}
          selected={isSelected(ProductTypeEnum.GuidedChoice)}
          recommended={isRecommended(ProductTypeEnum.GuidedChoice)}
          activeContract={activeContract && product === ProductTypeEnum.GuidedChoice}
        />
        <ProductCard
          bullets={[
            'Financial planning',
            'Robust financial education',
            'Available on desktop and mobile',
          ]}
          art={<Svgs.ArtChipPieChart />}
          product={ProductTypeEnum.Nickels}
          setSelectedProduct={setSelectedProduct}
          selected={isSelected(ProductTypeEnum.Nickels)}
          recommended={isRecommended(ProductTypeEnum.Nickels)}
          activeContract={activeContract && product === ProductTypeEnum.Nickels}
        />
      </Box>

      <ButtonFooter
        primaryButton={{
          label: 'Next: Licenses',
          onClick: navToNextStep,
          disabled: selectedProduct === undefined && !activeContract,
        }}
        secondaryButton={{
          label: 'Back',
          onClick: navBack,
          showCondition: true,
        }}
        tertiaryButton={{
          showCondition: activeContract,
          component: <CancelContract />,
        }}
      />
    </Column>
  );
};

export default ChooseProduct;
